import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import {
  Col,
  Row,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import useFullPageLoader from "../../hooks/useFullPageLoader";

function ConfiguracionSoporte() {
  const { user } = useContext(AuthContext);
  const URL_SUPPORT_CONFIG = process.env.REACT_APP_URL_SUPPORT_CONFIG;

  const [config, setConfig] = useState(null);
  const [configId, setConfigId] = useState("");
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Campos editables
  const [sistemifyNumber, setSistemifyNumber] = useState("");
  const [aiGlobalEnabled, setAiGlobalEnabled] = useState(true);
  const [aiDefaultDelay, setAiDefaultDelay] = useState(30);
  const [aiDefaultModel, setAiDefaultModel] = useState("claude-sonnet-4-5-20250514");
  const [escalationChannel, setEscalationChannel] = useState("whatsapp_dm");
  const [escalationNumber, setEscalationNumber] = useState("");

  // Números del equipo
  const [newTeamNumber, setNewTeamNumber] = useState("");
  const [teamNumbers, setTeamNumbers] = useState([]);

  // Clusters
  const [modalCluster, setModalCluster] = useState(false);
  const [newClusterName, setNewClusterName] = useState("");
  const [newClusterEnvVar, setNewClusterEnvVar] = useState("");
  const [newClusterDescription, setNewClusterDescription] = useState("");
  const [clusters, setClusters] = useState({});

  const [validaBoton, setValidaBoton] = useState(true);

  useEffect(() => {
    getConfig();
  }, []);

  const getConfig = () => {
    showLoader();
    axios
      .get(URL_SUPPORT_CONFIG, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const data = response.data;
        setConfig(data);
        setConfigId(data._id);
        setSistemifyNumber(data.sistemifyNumber || "");
        setAiGlobalEnabled(data.aiGlobalEnabled ?? true);
        setAiDefaultDelay(data.aiDefaultDelay || 30);
        setAiDefaultModel(data.aiDefaultModel || "claude-sonnet-4-5-20250514");
        setEscalationChannel(data.notifications?.escalationChannel || "whatsapp_dm");
        setEscalationNumber(data.notifications?.escalationNumber || "");
        setTeamNumbers(data.teamNumbers || []);
        
        // Convertir Map a objeto
        if (data.mongoClusters) {
          const clustersObj = {};
          if (data.mongoClusters instanceof Map) {
            data.mongoClusters.forEach((value, key) => {
              clustersObj[key] = value;
            });
          } else if (typeof data.mongoClusters === 'object') {
            Object.assign(clustersObj, data.mongoClusters);
          }
          setClusters(clustersObj);
        }
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  };

  const saveConfig = async (event) => {
    event.preventDefault();
    setValidaBoton(false);

    const configData = {
      sistemifyNumber,
      aiGlobalEnabled,
      aiDefaultDelay,
      aiDefaultModel,
      notifications: {
        escalationChannel,
        escalationNumber,
      },
    };

    try {
      await axios.patch(
        `${URL_SUPPORT_CONFIG}/${configId}`,
        configData,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );
      Swal.fire("Éxito", "Configuración actualizada correctamente", "success");
      getConfig();
      setValidaBoton(true);
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "No se pudo guardar la configuración", "error");
      setValidaBoton(true);
    }
  };

  const addTeamNumber = () => {
    if (!newTeamNumber) {
      Swal.fire("Error", "Ingrese un número válido", "error");
      return;
    }

    axios
      .post(
        `${URL_SUPPORT_CONFIG}Team/${configId}`,
        { number: newTeamNumber },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        Swal.fire("Éxito", "Número agregado correctamente", "success");
        setNewTeamNumber("");
        getConfig();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo agregar el número", "error");
      });
  };

  const removeTeamNumber = (number) => {
    Swal.fire({
      title: '¿Eliminar número?',
      text: `Se eliminará el número ${number}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${URL_SUPPORT_CONFIG}Team/${configId}/${number}`, {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          })
          .then((response) => {
            Swal.fire("Éxito", "Número eliminado correctamente", "success");
            getConfig();
          })
          .catch((err) => {
            console.log(err);
            Swal.fire("Error", "No se pudo eliminar el número", "error");
          });
      }
    });
  };

  const addCluster = () => {
    if (!newClusterName || !newClusterEnvVar) {
      Swal.fire("Error", "Complete todos los campos requeridos", "error");
      return;
    }

    axios
      .post(
        `${URL_SUPPORT_CONFIG}Cluster/${configId}`,
        {
          clusterName: newClusterName,
          envVar: newClusterEnvVar,
          description: newClusterDescription,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        Swal.fire("Éxito", "Cluster agregado correctamente", "success");
        setModalCluster(false);
        resetClusterModal();
        getConfig();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo agregar el cluster", "error");
      });
  };

  const removeCluster = (clusterName) => {
    Swal.fire({
      title: '¿Eliminar cluster?',
      text: `Se eliminará el cluster ${clusterName}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${URL_SUPPORT_CONFIG}Cluster/${configId}/${encodeURIComponent(clusterName)}`, {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          })
          .then((response) => {
            Swal.fire("Éxito", "Cluster eliminado correctamente", "success");
            getConfig();
          })
          .catch((err) => {
            console.log(err);
            Swal.fire("Error", "No se pudo eliminar el cluster", "error");
          });
      }
    });
  };

  const resetClusterModal = () => {
    setNewClusterName("");
    setNewClusterEnvVar("");
    setNewClusterDescription("");
  };

  if (!config) {
    return (
      <>
        <Header />
        {loader}
      </>
    );
  }

  return (
    <>
      <Header />
      {loader}
      {user.menu_soporte_config ? (
        <div className="container-fluid">
          <br />
          <Card>
            <CardHeader>
              <h3>Configuración Global del Sistema de Soporte</h3>
            </CardHeader>
            <CardBody>
              <Form onSubmit={saveConfig}>
                {/* Números del Equipo */}
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="text-primary">Números del Equipo Sistemify</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted">
                      Estos números serán identificados como miembros del equipo en todos los grupos de WhatsApp.
                    </p>
                    <Row>
                      <Col md={8}>
                        <FormGroup>
                          <Label>Agregar Número</Label>
                          <Input
                            type="text"
                            value={newTeamNumber}
                            onChange={(e) => setNewTeamNumber(e.target.value)}
                            placeholder="5215512345678"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <Label>&nbsp;</Label>
                        <Button
                          color="success"
                          onClick={addTeamNumber}
                          block
                          type="button"
                        >
                          <i className="fas fa-plus"></i> Agregar
                        </Button>
                      </Col>
                    </Row>
                    {teamNumbers.length > 0 ? (
                      <Table size="sm" bordered className="mt-3">
                        <thead>
                          <tr>
                            <th>Número</th>
                            <th style={{ width: "100px" }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamNumbers.map((number, index) => (
                            <tr key={index}>
                              <td>{number}</td>
                              <td>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeTeamNumber(number)}
                                  type="button"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted mt-3">No hay números del equipo configurados</p>
                    )}
                  </CardBody>
                </Card>

                {/* Número de Sistemify (Baileys) */}
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="text-primary">Número de Sistemify (Baileys)</h5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>Número de WhatsApp conectado *</Label>
                          <Input
                            type="text"
                            value={sistemifyNumber}
                            onChange={(e) => setSistemifyNumber(e.target.value)}
                            placeholder="5215512345678"
                            required
                          />
                          <small className="text-muted">
                            El número de teléfono que tiene la conexión de Baileys/WhatsApp Web
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Configuración Global de IA */}
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="text-primary">Configuración Global de IA</h5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={12}>
                        <FormGroup check className="mb-3">
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={aiGlobalEnabled}
                              onChange={(e) => setAiGlobalEnabled(e.target.checked)}
                            />{" "}
                            IA Global Habilitada
                          </Label>
                          <br />
                          <small className="text-muted">
                            Si está desactivada, ningún proyecto podrá usar IA aunque esté habilitada individualmente
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Delay Default (segundos)</Label>
                          <Input
                            type="number"
                            value={aiDefaultDelay}
                            onChange={(e) => setAiDefaultDelay(parseInt(e.target.value))}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={8}>
                        <FormGroup>
                          <Label>Modelo Default</Label>
                          <Input
                            type="text"
                            value={aiDefaultModel}
                            onChange={(e) => setAiDefaultModel(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Clusters de MongoDB */}
                <Card className="mb-4">
                  <CardHeader>
                    <Row>
                      <Col md={6}>
                        <h5 className="text-primary">Clusters de MongoDB</h5>
                      </Col>
                      <Col md={6} align="right">
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => setModalCluster(true)}
                          type="button"
                        >
                          <i className="fas fa-plus"></i> Agregar Cluster
                        </Button>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody>
                    {Object.keys(clusters).length > 0 ? (
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Variable de Entorno</th>
                            <th>Descripción</th>
                            <th style={{ width: "100px" }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(clusters).map(([name, data]) => (
                            <tr key={name}>
                              <td>
                                <Badge color="secondary">{name}</Badge>
                              </td>
                              <td><code>{data.envVar}</code></td>
                              <td>{data.description || "N/A"}</td>
                              <td>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeCluster(name)}
                                  type="button"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted">No hay clusters configurados</p>
                    )}
                  </CardBody>
                </Card>

                {/* Canal de Escalamiento */}
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="text-primary">Configuración de Escalamiento</h5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Canal de Escalamiento</Label>
                          <Input
                            type="select"
                            value={escalationChannel}
                            onChange={(e) => setEscalationChannel(e.target.value)}
                          >
                            <option value="whatsapp_dm">WhatsApp DM</option>
                            <option value="slack">Slack</option>
                            <option value="email">Email</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Número/Canal de Escalamiento</Label>
                          <Input
                            type="text"
                            value={escalationNumber}
                            onChange={(e) => setEscalationNumber(e.target.value)}
                            placeholder="5215512345678 o #channel o email@sistemify.com"
                          />
                          <small className="text-muted">
                            Donde enviar las notificaciones cuando la IA necesite ayuda
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Botones */}
                <br />
                <Row>
                  <Col md={12}>
                    <Button color="success" type="submit" disabled={!validaBoton}>
                      <i className="fas fa-save"></i> Guardar Configuración
                    </Button>{" "}
                    <Button color="secondary" href="/MenuSoporte">
                      <i className="fas fa-arrow-left"></i> Volver
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </div>
      ) : undefined}

      {/* Modal para agregar cluster */}
      <Modal isOpen={modalCluster} toggle={() => setModalCluster(!modalCluster)}>
        <ModalHeader toggle={() => setModalCluster(!modalCluster)}>
          Agregar Cluster de MongoDB
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nombre del Cluster *</Label>
            <Input
              type="text"
              value={newClusterName}
              onChange={(e) => setNewClusterName(e.target.value)}
              placeholder="Mongo 8"
            />
          </FormGroup>
          <FormGroup>
            <Label>Variable de Entorno *</Label>
            <Input
              type="text"
              value={newClusterEnvVar}
              onChange={(e) => setNewClusterEnvVar(e.target.value)}
              placeholder="MONGO8_URI"
            />
          </FormGroup>
          <FormGroup>
            <Label>Descripción</Label>
            <Input
              type="textarea"
              rows="3"
              value={newClusterDescription}
              onChange={(e) => setNewClusterDescription(e.target.value)}
              placeholder="Clientes: Crembo, Difference, Luxurette..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={addCluster}>
            Agregar
          </Button>{" "}
          <Button color="secondary" onClick={() => {
            setModalCluster(false);
            resetClusterModal();
          }}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ConfiguracionSoporte;
